<template>
  <el-card shadow="never" :class="{ 'tech-card': tech }">
    <template v-if="!tech" #header>
      <CardTitle title="用户统计" />
    </template>
    <Echart :height="300" :options="lineChartOptions" />
  </el-card>
</template>
<script lang="ts" setup>
import dayjs from 'dayjs'
import { EChartsOption } from 'echarts'
import * as MemberStatisticsApi from '@/api/mall/statistics/member'
import { formatDate } from '@/utils/formatTime'
import { CardTitle } from '@/components/Card'

/** 会员用户统计卡片 */
defineOptions({ name: 'MemberStatisticsCard' })

const props = withDefaults(
  defineProps<{
    tech?: boolean
  }>(),
  { tech: false }
)

const loading = ref(true)
const lineChartOptions = reactive<EChartsOption>({
  dataset: {
    dimensions: ['date', 'count'],
    source: []
  },
  grid: {
    left: 20,
    right: 20,
    bottom: 20,
    top: 80,
    containLabel: true
  },
  legend: {
    top: 50
  },
  series: [{ name: '注册量', type: 'line', smooth: true, areaStyle: {} }],
  toolbox: {
    feature: {
      saveAsImage: { show: true, name: '会员统计' }
    }
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'cross'
    },
    padding: [5, 10]
  },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    axisTick: {
      show: false
    },
    axisLabel: {
      formatter: (date: string) => formatDate(date, 'MM-DD')
    }
  },
  yAxis: {
    axisTick: {
      show: false
    }
  }
}) as EChartsOption

const applyTechTheme = () => {
  if (!props.tech) return
  const textColor = '#9fc4ee'
  lineChartOptions.textStyle = { color: textColor }
  ;(lineChartOptions.legend as Record<string, unknown>).textStyle = { color: textColor }
  ;(lineChartOptions.xAxis as Record<string, unknown>).axisLabel = {
    ...(lineChartOptions.xAxis as any).axisLabel,
    color: '#6f8fb3'
  }
  ;(lineChartOptions.yAxis as Record<string, unknown>).axisLabel = { color: '#6f8fb3' }
  ;(lineChartOptions.yAxis as Record<string, unknown>).splitLine = {
    lineStyle: { color: 'rgba(28,156,255,0.15)' }
  }
  lineChartOptions.color = ['#00f2fe']
  lineChartOptions.series = [
    {
      name: '注册量',
      type: 'line',
      smooth: true,
      areaStyle: { color: 'rgba(0,242,254,0.18)' }
    }
  ]
}

const getMemberRegisterCountList = async () => {
  loading.value = true
  const beginTime = dayjs().subtract(30, 'd').startOf('d')
  const endTime = dayjs().endOf('d')
  const list = await MemberStatisticsApi.getMemberRegisterCountList(beginTime, endTime)
  if (lineChartOptions.dataset && !(lineChartOptions.dataset instanceof Array)) {
    lineChartOptions.dataset.source = list
  }
  applyTechTheme()
  loading.value = false
}

onMounted(() => {
  getMemberRegisterCountList()
})
</script>

<style scoped lang="scss">
.tech-card {
  color: #e6f7ff;
  background: transparent;
  border: none;

  :deep(.el-card__body) {
    padding-top: 8px;
  }
}
</style>
